import { AxiosRequestConfig, AxiosResponse, RawAxiosRequestConfig } from "axios";
import { axiosInstance as axios } from "./axios-provider";
import { AbortRequestEventBus } from "../../event-bus";

async function curriedRequest<P = unknown, R = unknown>(
  request: (url: string, payload: P, config: AxiosRequestConfig) => Promise<AxiosResponse>,
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: P
): Promise<AxiosResponse<R>> {
  if (abortable) {
    const controller = new AbortController();
    const abortHandler = () => {
      controller.abort();
    };

    AbortRequestEventBus.on("abort", abortHandler);

    const cleanup = () => {
      AbortRequestEventBus.off("abort", abortHandler);
    };

    const requestPromise = request(url, payload, {
      signal: controller.signal,
      ...config,
    });

    try {
      const response = await requestPromise;
      cleanup();
      return response;
    } catch (error) {
      cleanup();
      throw error;
    }
  } else {
    return request(url, payload, { ...config });
  }
}

export async function curriedGet(
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: string
): Promise<AxiosResponse> {
  return curriedRequest(
    (url, data, config) => {
      return axios.get(url, config);
    },
    url,
    config,
    abortable,
    payload
  );
}

export async function curriedPost(
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: string
): Promise<AxiosResponse> {
  return curriedRequest(
    (url, data, config) => {
      return axios.post(url, data, config);
    },
    url,
    config,
    abortable,
    payload
  );
}

export async function curriedPut(
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: string
): Promise<AxiosResponse> {
  return curriedRequest(
    (url, data, config) => {
      return axios.put(url, data, config);
    },
    url,
    config,
    abortable,
    payload
  );
}

export async function curriedPatch(
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: string
): Promise<AxiosResponse> {
  return curriedRequest(
    (url, data, config) => {
      return axios.patch(url, data, config);
    },
    url,
    config,
    abortable,
    payload
  );
}

export async function curriedDelete(
  url: string,
  config: RawAxiosRequestConfig,
  abortable = true,
  payload?: string
): Promise<AxiosResponse> {
  return curriedRequest(
    (url, data, config) => {
      return axios.delete(url, config);
    },
    url,
    config,
    abortable,
    payload
  );
}
